import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { FeaturedHero } from "@/components/home/FeaturedHero";
import { NearbyEventCard } from "@/components/home/NearbyEventCard";
import { LiveAroundMap } from "@/components/home/LiveAroundMap";
import { PopularLiveThumb } from "@/components/home/PopularLiveThumb";
import { CategoriesMini } from "@/components/home/CategoriesMini";
import { PremiumEventCard } from "@/components/cards/PremiumEventCard";
import { StartingSoonCard } from "@/components/cards/StartingSoonCard";
import { SponsorsBar } from "@/components/home/SponsorsBar";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";

function ColHeader({ title, to, linkLabel = "View all" }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="font-heading text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
        {title}
      </h2>
      <Link
        to={to}
        data-testid={`view-all-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className="text-sm font-semibold text-brand hover:text-brand-hover"
      >
        {linkLabel}
      </Link>
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="mx-auto max-w-[1400px] space-y-8 px-5 py-8 sm:px-8">
      <Skeleton className="h-[420px] w-full rounded-3xl" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-2xl lg:col-span-2" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-56 rounded-2xl lg:col-span-2" />
        <Skeleton className="h-56 rounded-2xl" />
      </div>
    </div>
  );
}

export default function Home() {
  const { data, loading, error, refetch } = useMockQuery(() => apiClient.getHome(), []);
  const [featuredOverride, setFeaturedOverride] = React.useState(null);
  const [nearMeLoading, setNearMeLoading] = React.useState(false);

  // Haversine distance in km between two lat/lng points.
  const distanceKm = (aLat, aLng, bLat, bLng) => {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const handleNearMe = () => {
    const candidates = data?.featuredCandidates || [];
    // Priority guard: an admin-promoted live always wins over "nearby".
    const promoted = candidates
      .filter((c) => c.promoted)
      .sort((a, b) => b.viewers - a.viewers);
    if (promoted.length) {
      setFeaturedOverride(promoted[0]);
      toast.info("Showing the promoted live selected by WhooLive.");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't supported by your browser.");
      return;
    }
    const geoCandidates = candidates.filter(
      (c) => typeof c.lat === "number" && typeof c.lng === "number"
    );
    if (!geoCandidates.length) {
      toast.error("No live stream with a known location right now.");
      return;
    }
    setNearMeLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const closest = [...geoCandidates].sort(
          (a, b) =>
            distanceKm(latitude, longitude, a.lat, a.lng) -
            distanceKm(latitude, longitude, b.lat, b.lng)
        )[0];
        setFeaturedOverride(closest);
        setNearMeLoading(false);
        toast.success("Showing the live stream closest to you.");
      },
      () => {
        setNearMeLoading(false);
        toast.error("Location denied — showing the most watched live instead.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  if (loading)
    return (
      <MainLayout>
        <HomeSkeleton />
      </MainLayout>
    );

  if (error)
    return (
      <MainLayout>
        <div className="mx-auto max-w-3xl px-5 py-32">
          <ErrorState error={error} onRetry={refetch} />
        </div>
      </MainLayout>
    );

  const { featured, nearby, popular, mapEvents, categories, sponsors, premiumEvents, startingSoon } = data;
  const activeFeatured = featuredOverride || featured;

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1400px] space-y-10 px-5 py-8 sm:px-8">
        {/* Featured Live hero */}
        <FeaturedHero
          featured={activeFeatured}
          onNearMe={handleNearMe}
          nearMeLoading={nearMeLoading}
          nearMeActive={!!featuredOverride && !featuredOverride.promoted}
        />

        {/* Nearby events + Live around you */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="min-w-0 lg:col-span-2">
            <ColHeader title="Nearby events" to="/explore" />
            <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {nearby.map((e) => (
                <NearbyEventCard key={e.id} event={e} />
              ))}
            </div>
          </div>
          <div className="flex min-w-0 flex-col">
            <ColHeader title="Live around you" to="/map" linkLabel="View map" />
            <div className="min-h-[300px] flex-1">
              <LiveAroundMap events={mapEvents} />
            </div>
          </div>
        </div>

        {/* Popular live streams + Categories */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="min-w-0 lg:col-span-2">
            <ColHeader title="Popular live streams" to="/explore" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {popular.map((p) => (
                <PopularLiveThumb key={p.id} item={p} />
              ))}
            </div>
          </div>
          <div className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm">
            <ColHeader title="Categories" to="/explore?category=Music" />
            <CategoriesMini categories={categories} />
          </div>
        </div>

        {/* Starting Soon */}
        <div>
          <ColHeader title="Starting soon" to="/explore" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {startingSoon.map((s) => (
              <StartingSoonCard key={s.id} item={s} />
            ))}
          </div>
        </div>

        {/* Premium Events */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <h2 className="font-heading text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
                Premium events
              </h2>
              <span className="rounded-full bg-gradient-to-r from-amber-400 to-amber-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                Paid
              </span>
            </div>
            <Link
              to="/explore"
              data-testid="view-all-premium"
              className="text-sm font-semibold text-brand hover:text-brand-hover"
            >
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {premiumEvents.map((e) => (
              <PremiumEventCard key={e.id} event={e} />
            ))}
          </div>
        </div>

        {/* Sponsored */}
        <SponsorsBar sponsors={sponsors} />
      </div>
    </MainLayout>
  );
}
